import { useQuery } from '@tanstack/react-query';
import { Check, ImageOff, Loader2 } from 'lucide-react';
import React from 'react';

import { resolveMediaBatch } from '../../components/media.api';

import { listHeroInclusions } from '@/features/hero-inclusions/hero-inclusions.api';

/**
 * Checklist over the Hero Inclusions master (logo + name), for sections that
 * store inclusions by id. Each row previews the logo the website will draw, so
 * an editor picks the artwork rather than retyping a label.
 *
 * Picks append, and the stored order is the display order on the card.
 */

const STALE = 5 * 60_000;

interface HeroInclusionChecklistProps {
  label: string;
  /** Ordered hero-inclusion master ids as stored on the card. */
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

export const HeroInclusionChecklist = ({
  label,
  value,
  onChange,
  disabled,
}: HeroInclusionChecklistProps): React.JSX.Element => {
  const listQuery = useQuery({
    queryKey: ['hero-inclusions', 'cms-checklist'],
    queryFn: () =>
      listHeroInclusions({
        page: 1,
        pageSize: 200,
        sortBy: 'sortOrder',
        sortDir: 'asc',
        isActive: true,
      }),
    staleTime: STALE,
  });
  const rows = listQuery.data?.data ?? [];

  // One batch call for every logo on screen, same as the media fields do.
  const iconIds = rows.map((r) => r.iconImageId).filter((id): id is string => Boolean(id));
  const iconsQuery = useQuery({
    queryKey: ['hero-inclusions', 'cms-checklist-icons', [...iconIds].sort().join(',')],
    enabled: iconIds.length > 0,
    queryFn: () => resolveMediaBatch(iconIds),
    staleTime: STALE,
  });
  const iconUrls = Object.fromEntries((iconsQuery.data ?? []).map((m) => [m.id, m.url]));

  const selected = new Set(value);
  const toggle = (id: string): void => {
    if (disabled) return;
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-(--fg-muted)">{label}</label>
      <div className="max-h-56 overflow-y-auto rounded-md border border-(--border) p-1">
        {listQuery.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-xs text-(--fg-faint)">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading inclusions…
          </div>
        ) : listQuery.isError ? (
          <p className="py-6 text-center text-xs text-rose-600">
            Failed to load hero inclusions. Try again.
          </p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-xs text-(--fg-faint)">
            No active hero inclusions yet - add them under Masters &rarr; Hero Inclusions.
          </p>
        ) : (
          rows.map((r) => {
            const isPicked = selected.has(r.id);
            const iconUrl = r.iconImageId ? iconUrls[r.iconImageId] : undefined;
            const pickOrder = value.indexOf(r.id);
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => toggle(r.id)}
                disabled={disabled}
                className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-(--bg-subtle) ${
                  isPicked ? 'text-(--fg)' : 'text-(--fg-muted)'
                }`}
              >
                <span
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                    isPicked ? 'border-(--fg) bg-(--fg) text-(--bg)' : 'border-(--border)'
                  }`}
                >
                  {isPicked && <Check className="h-3 w-3" />}
                </span>
                {iconUrl ? (
                  <img src={iconUrl} alt="" className="h-5 w-5 shrink-0 object-contain" />
                ) : (
                  <ImageOff className="h-5 w-5 shrink-0 text-(--fg-faint)" />
                )}
                <span className="min-w-0 flex-1 truncate">{r.name}</span>
                {isPicked && (
                  <span className="shrink-0 text-[10px] font-semibold text-(--fg-faint)">
                    {pickOrder + 1}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
      <p className="mt-1 text-[11px] text-(--fg-faint)">
        Ticks show each inclusion&apos;s logo from Masters &rarr; Hero Inclusions. Rows without a
        logo fall back to a green check on the website.
      </p>
    </div>
  );
};
